/**
 * Màn **Chân dung khách**.
 *
 * Hai nửa, và chúng **không được trộn**:
 *
 * - `moTaNguoiViet` — Lynust tự viết. **Nguồn chính.**
 * - `moTaAiDucKet` — AI đúc kết từ số đo GA4. **Rỗng cho tới khi có đủ dữ liệu.**
 *
 * Rỗng ở nửa thứ hai **không** nghĩa là "không ai đọc site này" — nó nghĩa là *chưa đúc kết lần
 * nào*. Hai chuyện đó phải hiện khác nhau, nếu không một ô trống bị đọc thành một kết luận.
 *
 * **Hai tầng, đọc từ trên xuống:** *site này viết về cái gì* → *viết cho ai*. Cả hai câu đều đi
 * thẳng vào prompt, nên chúng ở cùng một màn. Trước 09/09/2026 câu thứ nhất nằm ở màn Cấu trúc
 * prompt — sai chỗ, vì nó là hồ sơ của site chứ không phải cấu trúc của prompt, và nó khiến màn
 * kia trông như có việc làm trong khi màn kia chưa có công tắc nào.
 *
 * ⚠️ **Chân dung 100% từ Google Analytics, KHÔNG lấy Facebook** (`CLAUDE.md` §5.17). Một fanpage
 * chung nhận traffic của cả 5 web nên chân dung phía Facebook bị trộn. Đây là chỗ AutoIME **đảo
 * ngược** kết luận của bản mẫu — bên đó lấy nhân khẩu học từ Meta Insights — và lý do ghi ngay ở
 * đây để người đọc sau không "sửa lại cho giống bản mẫu".
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, type ChanDung as Nguoi, type HoSoSite } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { gioVN } from "../lib/nhan.ts";
import {
  Bao,
  Chip,
  ChuaDo,
  DangTai,
  DauThe,
  DauTrang,
  Khoi,
  Nut,
  Rong,
  The,
  Truong,
  lopO,
} from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

interface BanNhap {
  ten: string;
  moTaNguoiViet: string;
  siteSlug: string;
}

const TRONG: BanNhap = { ten: "", moTaNguoiViet: "", siteSlug: "" };

export function ChanDung() {
  const qc = useQueryClient();
  const toast = useToast();
  const [dangSua, datDangSua] = useState<string | null>(null);
  const [ban, datBan] = useState<BanNhap>(TRONG);
  const [themMoi, datThemMoi] = useState(false);

  const { data, isPending, error } = useQuery({ queryKey: khoa.chanDung, queryFn: api.chanDung });

  const xong = () => {
    datDangSua(null);
    datThemMoi(false);
    datBan(TRONG);
    void qc.invalidateQueries({ queryKey: khoa.chanDung });
  };

  const them = useMutation({
    mutationFn: () => api.themChanDung(ban),
    onSuccess: () => {
      xong();
      toast.on("Đã thêm chân dung");
    },
    onError: (loi: Error) => toast.loi("Không thêm được", loi.message),
  });
  const sua = useMutation({
    mutationFn: () => api.suaChanDung(dangSua as string, ban),
    onSuccess: () => {
      xong();
      toast.on("Đã lưu chân dung");
    },
    onError: (loi: Error) => toast.loi("Không lưu được", loi.message),
  });

  if (error) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Chân dung khách" />
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </div>
    );
  }
  if (isPending) {
    return (
      <div className="space-y-4">
        <DauTrang tieuDe="Chân dung khách" />
        <DangTai />
      </div>
    );
  }

  const dangMoForm = themMoi || dangSua !== null;
  const hopLe = ban.ten.trim() !== "" && ban.moTaNguoiViet.trim() !== "";

  return (
    <div className="min-w-0 space-y-5">
      <DauTrang
        tieuDe="Chân dung khách"
        moTa="Đi vào prompt cùng mô tả site và bài học. Anh tự viết; phần AI đúc kết là một cột riêng, không ghi đè."
      >
        {!dangMoForm && (
          <Nut
            kieu="chinh"
            onClick={() => {
              datBan(TRONG);
              datThemMoi(true);
            }}
          >
            Thêm chân dung
          </Nut>
        )}
      </DauTrang>

      <HoSoSiteKhoi />

      <Bao muc="thuong" tieuDe="Chỉ lấy từ Google Analytics, không lấy Facebook">
        Một fanpage chung nhận traffic của cả 5 web, nên chân dung phía Facebook bị <strong>trộn
        </strong> — không tách được người đọc laerdal với người đọc miradry. Facebook vẫn dùng để{" "}
        <strong>đo bài đã đăng</strong>, không dùng để suy ra chân dung.
      </Bao>

      {dangMoForm && (
        <The vien="canh">
          <DauThe tieuDe={themMoi ? "Chân dung mới" : "Sửa chân dung"} />

          <div className="space-y-3">
            <Truong nhan="Tên nhóm">
              <input
                value={ban.ten}
                onChange={(e) => datBan((c) => ({ ...c, ten: e.target.value }))}
                placeholder="Ví dụ: Điều dưỡng trưởng khoa hồi sức"
                className={lopO}
              />
            </Truong>

            <Truong
              nhan="Anh mô tả nhóm này"
              goiY="Họ làm gì, quyết định mua dựa trên cái gì, đọc để tìm điều gì."
            >
              <textarea
                value={ban.moTaNguoiViet}
                onChange={(e) => datBan((c) => ({ ...c, moTaNguoiViet: e.target.value }))}
                rows={4}
                className={lopO}
              />
            </Truong>

            <Truong nhan="Site" goiY="Bỏ trống = áp cho mọi site.">
              <select
                value={ban.siteSlug}
                onChange={(e) => datBan((c) => ({ ...c, siteSlug: e.target.value }))}
                className={lopO}
              >
                <option value="">(mọi site)</option>
                {data.sites.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </Truong>

            <div className="flex flex-wrap gap-2">
              <Nut
                kieu="chinh"
                dangChay={them.isPending || sua.isPending}
                disabled={!hopLe}
                onClick={() => (themMoi ? them.mutate() : sua.mutate())}
              >
                Lưu
              </Nut>
              <Nut kieu="vien" onClick={xong}>
                Huỷ
              </Nut>
            </div>

          </div>
        </The>
      )}

      {data.ds.length === 0 ? (
        <Rong tieuDe="Chưa có chân dung nào">
          Chưa phải lỗi — prompt vẫn chạy được, chỉ là thiếu một nguồn. Thêm một nhóm thì bài sau
          sẽ viết cho đúng người.
        </Rong>
      ) : (
        <div className="grid min-w-0 gap-4 lg:grid-cols-2">
          {data.ds.map((p: Nguoi) => (
            <The key={p._id}>
              <DauThe tieuDe={p.ten} phu={p.siteSlug ?? "mọi site"}>
                <Nut
                  kieu="vien"
                  onClick={() => {
                    datBan({
                      ten: p.ten,
                      moTaNguoiViet: p.moTaNguoiViet,
                      siteSlug: p.siteSlug ?? "",
                    });
                    datThemMoi(false);
                    datDangSua(p._id);
                  }}
                >
                  Sửa
                </Nut>
              </DauThe>

              <p className="text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
                anh viết
              </p>
              <p className="mt-1 min-w-0 whitespace-pre-wrap break-words text-sm text-muc-800">
                {p.moTaNguoiViet || <span className="text-muc-400">(trống)</span>}
              </p>

              <p className="mt-3 flex items-center gap-2 text-2xs font-medium uppercase tracking-[0.1em] text-muc-500">
                AI đúc kết từ GA4
                {p.lanDucKetCuoi !== null && <Chip muc="thuong">{gioVN(p.lanDucKetCuoi)}</Chip>}
              </p>
              <div className="mt-1 min-w-0 text-sm text-muc-700">
                {p.moTaAiDucKet ? (
                  <p className="whitespace-pre-wrap break-words">{p.moTaAiDucKet}</p>
                ) : (
                  // "Chưa đúc kết lần nào" ≠ "đúc kết ra kết quả rỗng". Dấu gạch có `title` nói
                  // rõ chuyện đó, thay vì để một ô trắng bị đọc thành một kết luận.
                  <p className="text-2xs text-muc-500">
                    <ChuaDo nhan="chưa đúc kết lần nào" /> Chưa đúc kết — cần dữ liệu GA4, mà GA4
                    chưa nối (G7 sau).
                  </p>
                )}
              </div>
            </The>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────────
 * Hồ sơ site — câu "site này viết về cái gì"
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Câu này vào prompt **NGUYÊN VĂN**, cạnh slug, ở **mọi** bài của site đó.
 *
 * Bản mẫu đã trả giá vì chỉ đưa slug máy nhận diện vào prompt: AI đẻ ra chân dung cho mặt hàng
 * cửa hàng không bán. Một cái slug không nói được `laerdal` là mô hình đào tạo y khoa.
 *
 * Ở đây **chỉ có mô tả** — số bài mỗi kỳ nằm ở Cài đặt (`CLAUDE.md` §5.18: nhịp đăng là ô nhập ở
 * Cài đặt). Hai con số cùng một chỗ thì màn này trả lời hai câu hỏi khác nhau cùng lúc.
 */
function HoSoSiteKhoi() {
  const qc = useQueryClient();
  const toast = useToast();
  const [sua, datSua] = useState<Record<string, string>>({});
  const [vuaLuu, datVuaLuu] = useState<string | null>(null);

  const { data, isPending, error } = useQuery({ queryKey: khoa.hoSoSite, queryFn: api.hoSoSite });

  const luu = useMutation({
    mutationFn: (v: { site: string; moTaChuDe: string }) =>
      api.suaHoSoSite(v.site, { moTaChuDe: v.moTaChuDe }),
    onError: (loi: Error, v) => toast.loi(`Không lưu được hồ sơ ${v.site}`, loi.message),
    onSuccess: (kq) => {
      datVuaLuu(kq.siteSlug);
      datSua((cu) => {
        const m = { ...cu };
        delete m[kq.siteSlug];
        return m;
      });
      void qc.invalidateQueries({ queryKey: khoa.hoSoSite });
      void qc.invalidateQueries({ queryKey: khoa.cauTrucPrompt });
    },
  });

  if (isPending) {
    return (
      <Khoi nhan="Site này viết về cái gì" id="ho-so-site" moSan tomTat="…">
        <DangTai />
      </Khoi>
    );
  }
  if (error) {
    return (
      <Khoi nhan="Site này viết về cái gì" id="ho-so-site" moSan>
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
        </Bao>
      </Khoi>
    );
  }

  const thieu = data.filter((h) => h.moTaChuDe.trim() === "");

  return (
    <Khoi
      nhan="Site này viết về cái gì"
      id="ho-so-site"
      moSan
      tomTat={
        thieu.length > 0 ? (
          <span className="text-canh-600">{thieu.length} site chưa có mô tả</span>
        ) : (
          `${data.length}/${data.length} site đã có mô tả`
        )
      }
    >
      <p className="text-xs text-muc-600">
        Câu này đi vào prompt <strong>nguyên văn</strong>, cạnh slug, ở mọi bài của site đó. Càng cụ
        thể thì bài càng đúng giọng — một cái slug không nói được <code>laerdal</code> là mô hình
        đào tạo y khoa.
      </p>

      {thieu.length > 0 && (
        <Bao muc="canh" tieuDe={`${thieu.length} site chưa có mô tả`}>
          {thieu.map((h) => h.siteSlug).join(" · ")} — khi tới lượt sinh bài, những site này sẽ{" "}
          <strong>ném lỗi</strong> chứ không viết bài rỗng. Đó là hành vi đúng, nhưng nó nghĩa là
          hạn ngạch của chúng không chạy.
        </Bao>
      )}

      <div className="mt-3 space-y-3">
        {data.map((h: HoSoSite) => {
          const moTa = sua[h.siteSlug] ?? h.moTaChuDe;
          const daDoi = sua[h.siteSlug] !== undefined;

          return (
            <The key={h.siteSlug} vien={h.moTaChuDe.trim() === "" ? "canh" : "thuong"}>
              <DauThe
                tieuDe={h.siteSlug}
                phu={`${h.soBaiDaViet} bài đã viết · nhịp ${h.baiMoiKy} bài mỗi ${
                  h.donViKy === "tuan" ? "tuần" : "ngày"
                }`}
              >
                <div className="flex shrink-0 items-center gap-1.5">
                  {h.batBuocSongNgu && (
                    <Chip muc="canh" title="Thiếu bản tiếng Anh là 400 bên Website IME">
                      song ngữ
                    </Chip>
                  )}
                  {vuaLuu === h.siteSlug && !daDoi && <Chip muc="on">đã lưu</Chip>}
                </div>
              </DauThe>

              <Truong
                nhan="Site này viết về cái gì"
                goiY="Bán/phân phối cái gì, cho ai, khác các site còn lại ở điểm nào."
                loi={moTa.trim() === "" ? "Trống — site này sẽ không sinh được bài." : undefined}
              >
                <textarea
                  value={moTa}
                  onChange={(e) => datSua((cu) => ({ ...cu, [h.siteSlug]: e.target.value }))}
                  rows={3}
                  placeholder={`Ví dụ: ${h.siteSlug} phân phối …, người đọc là …`}
                  className={lopO}
                />
              </Truong>

              <div className="mt-2">
                <Nut
                  kieu={daDoi ? "chinh" : "vien"}
                  // Năm site, năm nút Lưu. `isPending` một mình làm cả năm cùng quay.
                  dangChay={luu.isPending && luu.variables?.site === h.siteSlug}
                  disabled={!daDoi}
                  onClick={() => luu.mutate({ site: h.siteSlug, moTaChuDe: moTa })}
                >
                  Lưu
                </Nut>
              </div>
            </The>
          );
        })}
      </div>


      <p className="mt-3 text-2xs text-muc-500">
        Số bài mỗi ngày hoặc mỗi tuần nằm ở <strong>Cài đặt → Nhịp đăng mỗi site</strong> — đó là
        cấu hình vận hành, không phải câu đi vào prompt.
      </p>
    </Khoi>
  );
}
